import { Controller } from "@hotwired/stimulus"

// "So a serving is 2 slices — does that match the label?"
//
// The inline modal captures exactly two units, a serving and one named
// thing, so unit_gate can hold both in named targets and derive the ratio
// between them. This page holds as many as a food has — 1,669 foods carry
// two or more and some carry five — so the same question has to be asked of
// a LIST rather than a pair.
//
// DERIVED, NEVER STORED, for the same reason it is in the modal: the ratio
// is 88 ÷ 44, computed from two weights already on screen. No column,
// nothing to go stale, and correcting the slice updates the answer.
//
// The point is not arithmetic — it is the error check. A mistyped weight
// comes out at 1.7 slices rather than 2, and staying silent is what makes
// that visible.
export default class extends Controller {
  static targets = ["row", "derived"]

  // Matches FoodUnit::RATIO_TOLERANCE, passed in rather than repeated.
  static values = { tolerance: { type: Number, default: 0.015 } }

  connect() {
    this.derive()
  }

  derive() {
    if (!this.hasDerivedTarget) return

    const units = this.rowTargets
      .map((row) => ({
        name: row.querySelector("[data-unit-picker-target='input']")?.value.trim(),
        amount: parseFloat(row.querySelector("[data-unit-picker-target='amount']")?.value)
      }))
      .filter((unit) => unit.name && unit.amount > 0)

    // A SERVING IS THE ONE WORTH CHECKING AGAINST. It is what a label
    // prints, and what every other unit on the food divides into — "a
    // serving is 2 slices" is the sentence people can verify, where "a
    // slice is half a serving" is the same fact nobody says out loud.
    const serving = units.find((unit) => unit.name.toLowerCase() === "serving")
    const others = units.filter((unit) => unit !== serving)

    const lines = serving
      ? others
          .map((unit) => this.sentence(serving, unit))
          .filter(Boolean)
      : []

    this.derivedTarget.innerHTML = ""
    this.derivedTarget.hidden = lines.length === 0
    lines.forEach((line) => this.derivedTarget.append(line))
  }

  sentence(serving, unit) {
    const ratio = this.wholeRatio(serving.amount, unit.amount)
    if (!ratio) return null

    const plural = ratio === 1 ? unit.name : `${unit.name}s`

    const line = document.createElement("p")
    line.className = "flex items-start gap-2"

    line.append(this.icon())

    const text = document.createElement("span")
    text.append(document.createTextNode("So a serving is "))

    const figure = document.createElement("b")
    figure.className = "font-semibold text-brand-deep"
    figure.textContent = `${ratio} ${plural}`
    text.append(figure)

    text.append(document.createTextNode(" — does that match the label?"))
    line.append(text)

    return line
  }

  // Lucide `circle-help`, as the modal draws it.
  icon() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    Object.entries({
      width: "14", height: "14", viewBox: "0 0 24 24", fill: "none",
      stroke: "currentColor", "stroke-width": "2.5", "stroke-linecap": "round",
      "stroke-linejoin": "round", "aria-hidden": "true"
    }).forEach(([key, value]) => svg.setAttribute(key, value))
    svg.classList.add("mt-px", "shrink-0", "text-ink-faint")

    const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle")
    circle.setAttribute("cx", "12")
    circle.setAttribute("cy", "12")
    circle.setAttribute("r", "9")

    const mark = document.createElementNS("http://www.w3.org/2000/svg", "path")
    mark.setAttribute("d", "M12 17h.01M12 13.5a1.5 1.5 0 0 1 1-1.4 2.6 2.6 0 1 0-3.2-3.4")

    svg.append(circle, mark)
    return svg
  }

  // Mirrors FoodUnit.whole_ratio. "2 slices" is worth confirming; "8.57
  // bars" is noise, and it is also the shape a mistyped weight takes.
  wholeRatio(larger, smaller) {
    if (!(larger > 0) || !(smaller > 0)) return null

    const ratio = larger / smaller
    if (ratio < 1) return null

    const nearest = Math.round(ratio)
    if (nearest === 0) return null
    if (Math.abs((ratio - nearest) / nearest) > this.toleranceValue) return null

    return nearest
  }
}
